import SuspenseWrapper from '@src/components/suspenseWrapper';
import Loader from '@src/components/loader';
import lazyWithPreload from '@src/utils/lazyWithPreload';
import { Navigate, RouteObject } from 'react-router-dom';
import Error from '@src/pages/error';
import Box from '@sendsprint/ui-react/dist/components/Box';
import { Path } from '../../routes';
import { PermissionName } from '@src/contexts/permission-context';
import { userHasPermission } from '@src/utils/userHasPermission';

export const Dashboard = lazyWithPreload(() => import('@src/pages/admin/dashboard'));
export const Senders = lazyWithPreload(() => import('@src/pages/admin/senders/senders'));
export const SendersDetailIdPage = lazyWithPreload(
  () => import('@src/pages/admin/senders/sendersDetails')
);

export const Recipients = lazyWithPreload(() => import('@src/pages/admin/recipients/recipients'));
export const RecipientsDetailIdPage = lazyWithPreload(
  () => import('@src/pages/admin/recipients/recipientsDetails')
);

export const Transactions = lazyWithPreload(
  () => import('@src/pages/admin/transactions/transactions')
);
export const TransferTransactionsDetailIdPage = lazyWithPreload(
  () => import('@src/pages/admin/transactions/transferTransactionDetails')
);
export const GiftTransactionsDetailIdPage = lazyWithPreload(
  () => import('@src/pages/admin/transactions/giftTransactionDetails')
);

export const CodesPage = lazyWithPreload(() => import('@src/pages/admin/codes/codes'));
export const PaymentCodesDetailsIdPage = lazyWithPreload(
  () => import('@src/pages/admin/codes/paymentCodeDetails')
);
export const DiscountCodesDetailsIdPage = lazyWithPreload(
  () => import('@src/pages/admin/codes/discountCodesDetails')
);

export const RateAndFeePage = lazyWithPreload(
  () => import('@src/pages/admin/ratesAndCodes/rateAndFees')
);
export const RateAndFeesDetailIdPage = lazyWithPreload(
  () => import('@src/pages/admin/ratesAndCodes/rateAndFeeDetails')
);
export const BusinessModulePage = lazyWithPreload(
  () => import('@src/pages/admin/businessModule/business')
);
export const BusinessDetailIdPage = lazyWithPreload(
  () => import('@src/pages/admin/businessModule/businessDetails')
);

export const BusinessUserPage = lazyWithPreload(
  () => import('@src/pages/admin/businessUser/businessUser')
);
export const BusinessUserDetailIdPage = lazyWithPreload(
  () => import('@src/pages/admin/businessUser/businessUserDetails')
);

export const BusinessTransactionPage = lazyWithPreload(
  () => import('@src/pages/admin/businessTransactions/businessTransactions')
);
export const BusinessRecieverTransactionIdPage = lazyWithPreload(
  () => import('@src/pages/admin/businessTransactions/businessTransactionReceiver')
);
export const BusinessSenderTransactionIdPage = lazyWithPreload(
  () => import('@src/pages/admin/businessTransactions/businessTransactionSender')
);

export const BusinessRecipientsPage = lazyWithPreload(
  () => import('@src/pages/admin/businessRecipients/businessRecipients')
);
export const BusinessRecipientsIdPage = lazyWithPreload(
  () => import('@src/pages/admin/businessRecipients/businessRecipientDetails')
);

export const BusinessCustomersPage = lazyWithPreload(
  () => import('@src/pages/admin/businessCustomers/businessCustomers')
);
export const BusinessCustomersIdPage = lazyWithPreload(
  () => import('@src/pages/admin/businessCustomers/businessCustomerDetails')
);

export const AdminUsersPage = lazyWithPreload(() => import('@src/pages/admin/adminUsers/admins'));
export const AminUsersDetailsIdPage = lazyWithPreload(
  () => import('@src/pages/admin/adminUsers/adminsDetails')
);

export const AgentsPage = lazyWithPreload(() => import('@src/pages/admin/agents/agents'));
export const AgentsDetailsPage = lazyWithPreload(
  () => import('@src/pages/admin/agents/agentsDetails')
);
export const AgentTransactionsPage = lazyWithPreload(
  () => import('@src/pages/admin/agentTransactions/agentTransactions')
);
export const AgentPayoutDetailsPage = lazyWithPreload(
  () => import('@src/pages/admin/agentTransactions/agentPayoutDetails')
);
export const AgentCollectionDetailsPage = lazyWithPreload(
  () => import('@src/pages/admin/agentTransactions/agentCollectionDetails')
);
export const AgentCustomersPage = lazyWithPreload(
  () => import('@src/pages/admin/agentCustomers/agentCustomers')
);
export const AgentCustomersDetailsPage = lazyWithPreload(
  () => import('@src/pages/admin/agentCustomers/agentCustomersDetails')
);

export const PermissionPage = lazyWithPreload(
  () => import('@src/pages/admin/permission/permission')
);
export const PermissionDetailsPage = lazyWithPreload(
  () => import('@src/pages/admin/permission/permissionDetails')
);
export const ActivitiesLogPage = lazyWithPreload(() => import('@src/pages/admin/activityLogs'));

const routesData: { path: string; component: JSX.Element; permissionViewName?: PermissionName }[] =
  [
    // Dashboard
    { path: Path.Dashboard, component: <Dashboard /> },
    // Senders
    { path: Path.Senders, component: <Senders />, permissionViewName: 'SenderView' },
    {
      path: `${Path.Senders}/:id`,
      component: <SendersDetailIdPage />,
      permissionViewName: 'SenderView'
    },
    // Recipients
    { path: Path.Recipients, component: <Recipients />, permissionViewName: 'RecipientView' },
    {
      path: `${Path.Recipients}/:id`,
      component: <RecipientsDetailIdPage />,
      permissionViewName: 'RecipientView'
    },
    // Transactions
    { path: Path.Transactions, component: <Transactions />, permissionViewName: 'TransactionView' },
    {
      path: `${Path.Transactions}/transfer/:txref`,
      component: <TransferTransactionsDetailIdPage />,
      permissionViewName: 'TransactionView'
    },
    {
      path: `${Path.Transactions}/gift/:txref`,
      component: <GiftTransactionsDetailIdPage />,
      permissionViewName: 'TransactionView'
    },
    // Codes
    { path: Path.Codes, component: <CodesPage />, permissionViewName: 'CodeView' },
    {
      path: `${Path.CodesPaymentCode}/:code`,
      component: <PaymentCodesDetailsIdPage />,
      permissionViewName: 'CodeView'
    },
    {
      path: `${Path.CodesDiscountCode}/:code`,
      component: <DiscountCodesDetailsIdPage />,
      permissionViewName: 'CodeView'
    },
    // Rates and fees
    { path: Path.RateAndFee, component: <RateAndFeePage />, permissionViewName: 'RateAndFeeView' },
    {
      path: `${Path.RateAndFee}/:id`,
      component: <RateAndFeesDetailIdPage />,
      permissionViewName: 'RateAndFeeView'
    },
    // Business
    {
      path: Path.BusinessModule,
      component: <BusinessModulePage />,
      permissionViewName: 'BusinessView'
    },
    {
      path: `${Path.BusinessModule}/:id`,
      component: <BusinessDetailIdPage />,
      permissionViewName: 'BusinessView'
    },
    // Business users
    {
      path: Path.BusinessUser,
      component: <BusinessUserPage />,
      permissionViewName: 'BusinessUserView'
    },
    {
      path: `${Path.BusinessUser}/:email`,
      component: <BusinessUserDetailIdPage />,
      permissionViewName: 'BusinessUserView'
    },
    {
      path: Path.BusinessTransaction,
      component: <BusinessTransactionPage />,
      permissionViewName: 'BusinessTransactionView'
    },
    {
      path: `${Path.BusinessTransactionSender}/:txref`,
      component: <BusinessSenderTransactionIdPage />,
      permissionViewName: 'BusinessTransactionView'
    },
    {
      path: `${Path.BusinessTransactionReceiver}/:txref`,
      component: <BusinessRecieverTransactionIdPage />,
      permissionViewName: 'BusinessTransactionView'
    },
    // Business recipients
    {
      path: Path.BusinessRecipients,
      component: <BusinessRecipientsPage />,
      permissionViewName: 'BusinessRecipientView'
    },
    {
      path: `${Path.BusinessRecipients}/:id`,
      component: <BusinessRecipientsIdPage />,
      permissionViewName: 'BusinessRecipientView'
    },
    // Business customers
    {
      path: Path.BusinessCustomers,
      component: <BusinessCustomersPage />,
      permissionViewName: 'BusinessCustomerView'
    },
    {
      path: `${Path.BusinessCustomers}/:email`,
      component: <BusinessCustomersIdPage />,
      permissionViewName: 'BusinessCustomerView'
    },
    // Agents
    { path: Path.Agents, component: <AgentsPage />, permissionViewName: 'AgentView' },
    {
      path: `${Path.Agents}/:id`,
      component: <AgentsDetailsPage />,
      permissionViewName: 'AgentView'
    },
    {
      path: Path.AgentTransactions,
      component: <AgentTransactionsPage />,
      permissionViewName: 'AgentTransactionView'
    },
    {
      path: `${Path.AgentTransactionsPayout}/:id`,
      component: <AgentPayoutDetailsPage />,
      permissionViewName: 'AgentTransactionView'
    },
    {
      path: `${Path.AgentTransactionsCollection}/:id`,
      component: <AgentCollectionDetailsPage />,
      permissionViewName: 'AgentTransactionView'
    },
    {
      path: Path.AgentCustomers,
      component: <AgentCustomersPage />,
      permissionViewName: 'AgentCustomerView'
    },
    {
      path: `${Path.AgentCustomers}/:id`,
      component: <AgentCustomersDetailsPage />,
      permissionViewName: 'AgentCustomerView'
    },
    // Admin
    { path: Path.AdminUsers, component: <AdminUsersPage />, permissionViewName: 'AdminView' },
    {
      path: `${Path.AdminUsers}/:id`,
      component: <AminUsersDetailsIdPage />,
      permissionViewName: 'AdminView'
    },
    { path: Path.Permission, component: <PermissionPage />, permissionViewName: 'PermissionView' },
    {
      path: `${Path.Permission}/:id`,
      component: <PermissionDetailsPage />,
      permissionViewName: 'PermissionView'
    },
    { path: `${Path.ActivityLogs}`, component: <ActivitiesLogPage /> }
  ];

export const adminUserRoutes: RouteObject[] = routesData.map(
  ({ component, path, permissionViewName }) => ({
    path,
    element: userHasPermission(permissionViewName) ? (
      <SuspenseWrapper
        errorFallback={({ resetError }) => (
          <Box>
            <Error showHeader={false} resetError={resetError} />
          </Box>
        )}
        suspenseFallback={<Loader showTimedOutScreen />}>
        {component}
      </SuspenseWrapper>
    ) : (
      <Navigate to={Path.Dashboard} replace />
    )
  })
);
